/**
 * Auth Store
 *
 * Manages authentication state using Zustand.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '@/api/axios';
import i18n from '@/i18n';

const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: true,

      // Set tokens
      setTokens: (accessToken, refreshToken) => {
        set({ accessToken, refreshToken });
      },

      // Set user
      setUser: (user) => {
        set({ user, isAuthenticated: !!user });
      },

      // Login
      login: async (email, password) => {
        try {
          const response = await api.post('/auth/login', { email, password });
          const { user, tokens } = response.data;

          // Set i18n language based on user preference
          if (user.language) {
            i18n.changeLanguage(user.language);
            localStorage.setItem('userLanguage', user.language);
          }

          set({
            user,
            accessToken: tokens.accessToken,
            refreshToken: tokens.refreshToken,
            isAuthenticated: true,
          });

          return { success: true, user };
        } catch (error) {
          const message = error.response?.data?.message || 'Login failed';
          throw new Error(message);
        }
      },

      // Register
      register: async (username, email, password) => {
        try {
          const response = await api.post('/auth/register', {
            username,
            email,
            password,
          });
          return { success: true, message: response.data.message };
        } catch (error) {
          const message = error.response?.data?.message || 'Registration failed';
          throw new Error(message);
        }
      },

      // Logout
      logout: async () => {
        try {
          const { accessToken } = get();
          if (accessToken) {
            await api.post('/auth/logout');
          }
        } catch (error) {
          console.error('Logout error:', error);
        } finally {
          set({
            user: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,
          });
        }
      },

      // Refresh token
      refreshAccessToken: async () => {
        try {
          const { refreshToken } = get();
          if (!refreshToken) {
            throw new Error('No refresh token');
          }

          const response = await api.post('/auth/refresh', { refreshToken });
          const { tokens } = response.data;

          set({
            accessToken: tokens.accessToken,
            refreshToken: tokens.refreshToken,
          });

          return tokens.accessToken;
        } catch (error) {
          // Clear auth state on refresh failure
          set({
            user: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,
          });
          throw error;
        }
      },

      // Check auth status
      checkAuth: async () => {
        const { accessToken, refreshToken } = get();

        if (!accessToken && !refreshToken) {
          set({ isLoading: false });
          return;
        }

        try {
          // Try to get current user
          const response = await api.get('/auth/me');
          const user = response.data.user;

          console.log('checkAuth - User loaded:', user);
          console.log('checkAuth - User language:', user.language);

          // Set i18n language based on user preference
          if (user.language) {
            console.log('checkAuth - Setting i18n language to:', user.language);
            i18n.changeLanguage(user.language);
            localStorage.setItem('userLanguage', user.language);
            console.log('checkAuth - Current i18n language:', i18n.language);
          }

          set({
            user,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error) {
          // If access token expired, try refresh
          if (error.response?.status === 401 && refreshToken) {
            try {
              await get().refreshAccessToken();
              const response = await api.get('/auth/me');
              const user = response.data.user;

              // Set i18n language based on user preference
              if (user.language) {
                i18n.changeLanguage(user.language);
                localStorage.setItem('userLanguage', user.language);
              }

              set({
                user,
                isAuthenticated: true,
                isLoading: false,
              });
            } catch (refreshError) {
              set({
                user: null,
                accessToken: null,
                refreshToken: null,
                isAuthenticated: false,
                isLoading: false,
              });
            }
          } else {
            set({
              user: null,
              accessToken: null,
              refreshToken: null,
              isAuthenticated: false,
              isLoading: false,
            });
          }
        }
      },

      // Update user profile
      updateUser: (updates) => {
        set((state) => ({
          user: state.user ? { ...state.user, ...updates } : null,
        }));
      },

      // Verify email
      verifyEmail: async (token) => {
        try {
          const response = await api.post('/auth/verify-email', { token });
          return { success: true, message: response.data.message };
        } catch (error) {
          const message = error.response?.data?.message || 'Verification failed';
          throw new Error(message);
        }
      },

      // Resend verification email
      resendVerification: async (email) => {
        try {
          const response = await api.post('/auth/resend-verification', { email });
          return { success: true, message: response.data.message };
        } catch (error) {
          const message = error.response?.data?.message || 'Failed to resend email';
          throw new Error(message);
        }
      },

      // Forgot password
      forgotPassword: async (email) => {
        try {
          const response = await api.post('/auth/forgot-password', { email });
          return { success: true, message: response.data.message };
        } catch (error) {
          const message = error.response?.data?.message || 'Failed to send reset email';
          throw new Error(message);
        }
      },

      // Reset password
      resetPassword: async (token, password) => {
        try {
          const response = await api.post('/auth/reset-password', { token, password });
          return { success: true, message: response.data.message };
        } catch (error) {
          const message = error.response?.data?.message || 'Failed to reset password';
          throw new Error(message);
        }
      },

      // Update password
      updatePassword: async (currentPassword, newPassword) => {
        try {
          const response = await api.put('/auth/password', {
            currentPassword,
            newPassword,
          });

          // Update tokens from response
          if (response.data.tokens) {
            set({
              accessToken: response.data.tokens.accessToken,
              refreshToken: response.data.tokens.refreshToken,
            });
          }

          return { success: true, message: response.data.message };
        } catch (error) {
          const message = error.response?.data?.message || 'Failed to update password';
          throw new Error(message);
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
      }),
    }
  )
);

export { useAuthStore };
